/* eslint-disable react/jsx-no-bind */
/* eslint-disable camelcase */
/* eslint-disable react/no-array-index-key */
import { useState, useEffect, useContext } from 'react';

import { Card, Text } from '@gympass/yoga';
import { ChevronDown, ChevronUp } from '@gympass/yoga-icons';
import { Close } from '@mui/icons-material';
import { isWithinInterval } from 'date-fns';
import isEqual from 'lodash.isequal';
import { useIntl, FormattedMessage } from 'react-intl';

import { AgendaContext, NotificationContext } from 'context';
import useRequest from 'hooks/useRequest';
import {
  AllowDayAndTime,
  getSpecialistAvailability,
  updateRecurrentAvailability,
} from 'services/specialist';

import { DropdownOption, RecurrentAvailability } from './types';

import * as S from './styles';

import { LinesLoading } from '..';

const RecurrentAvailabilityCard: React.FC = () => {
  const intl = useIntl();

  const updateAvailabilityRequest = useRequest();
  const getRecurrentAvailabilityRequest = useRequest();

  const { agenda, setAgenda } = useContext(AgendaContext);
  const { setNotification } = useContext(NotificationContext);

  const [daysAux] = useState<Record<string, number>>({
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  });

  const MAX_LIST_LENGTH = 10;

  const [availabilities, setAvailabilities] = useState<RecurrentAvailability[]>(
    [],
  );

  const [displayedAvailabilities, setDisplayedAvailabilities] = useState<
    RecurrentAvailability[]
  >([]);
  const [isListExpanded, setIsListExpanded] = useState<boolean>(false);
  const [selectedDay, setSelectedDay] = useState<string>('');
  const [selectedStartHour, setSelectedStartHour] = useState<string>('00:00');
  const [selectedEndHour, setSelectedEndHour] = useState<string>('00:00');
  const [hoursOptions] = useState<DropdownOption[]>(() => {
    const newHoursValue = [];

    for (let i = 0; i < 24; i += 1) {
      const hoursFormatted = [
        `${i.toString().padStart(2, '0')}:00`,
        `${i.toString().padStart(2, '0')}:15`,
        `${i.toString().padStart(2, '0')}:30`,
        `${i.toString().padStart(2, '0')}:45`,
      ];
      const options: DropdownOption[] = hoursFormatted.map(h => ({
        label: h,
        value: h,
      }));

      if (i === 0) {
        options[0].selected = true;
      }

      newHoursValue.push(options);
    }

    newHoursValue.push({
      label: '24:00',
      value: '24:00',
    });

    return newHoursValue.flat();
  });

  function isLoading() {
    return (
      getRecurrentAvailabilityRequest.isLoading() ||
      updateAvailabilityRequest.isLoading()
    );
  }

  function filterReccurrentAvailability(
    constraints: AllowDayAndTime[],
  ): RecurrentAvailability[] {
    return constraints.filter(a => {
      return a.allow_day_and_time;
    }) as RecurrentAvailability[];
  }

  function isDuplicated(timeCourse: RecurrentAvailability) {
    return availabilities.findIndex(a => isEqual(a, timeCourse)) !== -1;
  }

  function timeCourseIntersect(timeCourse: RecurrentAvailability) {
    return (
      availabilities.findIndex(a => {
        const [timeCourseStartHour, timeCourseStartMinute] =
          timeCourse.allow_day_and_time.start.split(':');

        const timeCourseStart = new Date(
          0,
          0,
          daysAux[timeCourse.allow_day_and_time.day],
          parseInt(timeCourseStartHour, 10),
          parseInt(timeCourseStartMinute, 10),
        );

        const [aStartHour, aStartMinute] =
          a.allow_day_and_time.start.split(':');
        const aDateStart = new Date(
          0,
          0,
          daysAux[a.allow_day_and_time.day],
          parseInt(aStartHour, 10),
          parseInt(aStartMinute, 10),
        );

        const [timeCourseEndHour, timeCourseEndMinute] =
          timeCourse.allow_day_and_time.end.split(':');
        const timeCourseEnd = new Date(
          0,
          0,
          daysAux[timeCourse.allow_day_and_time.day],
          parseInt(timeCourseEndHour, 10),
          parseInt(timeCourseEndMinute, 10),
        );

        const [aEndHour, aEndMinute] = a.allow_day_and_time.end.split(':');
        const aDateEnd = new Date(
          0,
          0,
          daysAux[a.allow_day_and_time.day],
          parseInt(aEndHour, 10),
          parseInt(aEndMinute, 10),
        );

        /*
          We should subtract one second in order to not override end dates with start dates
          eg. 
          (09:00:00-10:00:00)+(10:00:00-11:00:00) should be possible to book as available
          therefore we should do (09:00:00-09:59:59)+(10:00:00-10:59:59)
        */
        aDateEnd.setSeconds(aDateEnd.getSeconds() - 1);
        timeCourseEnd.setSeconds(timeCourseEnd.getSeconds() - 1);

        return (
          isWithinInterval(timeCourseStart, {
            start: aDateStart,
            end: aDateEnd,
          }) ||
          isWithinInterval(timeCourseEnd, { start: aDateStart, end: aDateEnd })
        );
      }) !== -1
    );
  }

  async function handleCreateNewRecurrentAvailability() {
    if (selectedStartHour >= selectedEndHour) {
      setNotification({
        message: intl.formatMessage({
          id: 'RECURRENT_AVAILABILITY.NEW_AVAILABILITY.END_AHEAD_START',
        }),
        type: 'failure',
      });
      return;
    }

    if (selectedDay === '') {
      setNotification({
        message: intl.formatMessage({
          id: 'RECURRENT_AVAILABILITY.NEW_AVAILABILITY.NO_SELECTED_DAY',
        }),
        type: 'failure',
      });
      return;
    }

    const newTimeCourse = {
      allow_day_and_time: {
        day: selectedDay,
        start: selectedStartHour,
        end: selectedEndHour,
      },
    };

    if (isDuplicated(newTimeCourse) || timeCourseIntersect(newTimeCourse)) {
      setNotification({
        message: intl.formatMessage({
          id: 'RECURRENT_AVAILABILITY.NEW_AVAILABILITY.DUPLICITY',
        }),
        type: 'failure',
      });
      return;
    }

    try {
      updateAvailabilityRequest.setLoading();

      let newAvailabilityObejct;

      if (agenda?.availability_constraints) {
        newAvailabilityObejct = [
          ...agenda.availability_constraints,
          newTimeCourse,
        ];
      } else {
        newAvailabilityObejct = [newTimeCourse];
      }

      await updateRecurrentAvailability(newAvailabilityObejct);

      setAgenda({
        ...agenda,
        availability_constraints: newAvailabilityObejct,
      });
      updateAvailabilityRequest.setSuccess();
    } catch (e) {
      setNotification({
        message: intl.formatMessage({
          id: 'ERROR.TRY',
        }),
        type: 'failure',
      });

      if (typeof e === 'string') {
        updateAvailabilityRequest.setError(e);
      }
    }
  }

  async function getRecurrentAvailability() {
    try {
      getRecurrentAvailabilityRequest.setLoading();
      const data = await getSpecialistAvailability();

      setAgenda(data);

      getRecurrentAvailabilityRequest.setSuccess();
    } catch (e) {
      if (typeof e === 'string') {
        getRecurrentAvailabilityRequest.setError(e);
      }
    }
  }

  async function deleteAvailability(availability: RecurrentAvailability) {
    updateAvailabilityRequest.setLoading();
    try {
      if (agenda?.availability_constraints) {
        const newAvailabilities = [...agenda.availability_constraints];

        const availabilityToDeleteIndex =
          agenda.availability_constraints.findIndex(a => {
            return isEqual(a, availability);
          });

        newAvailabilities.splice(availabilityToDeleteIndex, 1);

        await updateRecurrentAvailability(newAvailabilities);

        setAgenda({
          ...agenda,
          availability_constraints: newAvailabilities,
        });
      }

      updateAvailabilityRequest.setSuccess();
    } catch (e) {
      setNotification({
        message: intl.formatMessage({
          id: 'ERROR.TRY',
        }),
        type: 'failure',
      });

      if (typeof e === 'string') {
        updateAvailabilityRequest.setError(e);
      }
    }
  }

  function handleAvailabilitySort(availabilityList: RecurrentAvailability[]) {
    return availabilityList.sort((a, b) => {
      const aDate = new Date(
        0,
        0,
        daysAux[a.allow_day_and_time.day],
        parseInt(a.allow_day_and_time.start.split(':')[0], 10),
      );
      const bDate = new Date(
        0,
        0,
        daysAux[b.allow_day_and_time.day],
        parseInt(b.allow_day_and_time.start.split(':')[0], 10),
      );

      if (aDate > bDate) {
        return 1;
      }

      if (aDate < bDate) {
        return -1;
      }

      return 0;
    });
  }

  function handleAvailabilityListVisualisation() {
    let availabilitiesToDisplay: RecurrentAvailability[] = [];

    const sortedAvailabilities = handleAvailabilitySort(availabilities);

    if (isListExpanded) {
      availabilitiesToDisplay = [
        ...sortedAvailabilities.slice(0, MAX_LIST_LENGTH),
      ];
    } else {
      availabilitiesToDisplay = [...sortedAvailabilities];
    }

    setDisplayedAvailabilities(availabilitiesToDisplay);
    setIsListExpanded(!isListExpanded);
  }

  useEffect(() => {
    if (agenda === null) {
      getRecurrentAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAvailabilities(
      filterReccurrentAvailability(
        agenda?.availability_constraints?.length
          ? agenda.availability_constraints
          : [],
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agenda]);

  useEffect(() => {
    let availabilitiesToDisplay: RecurrentAvailability[] = [];

    const sortedAvailabilities = handleAvailabilitySort(availabilities);

    if (isListExpanded) {
      availabilitiesToDisplay = [...sortedAvailabilities];
    } else {
      availabilitiesToDisplay = [
        ...sortedAvailabilities.slice(0, MAX_LIST_LENGTH),
      ];
    }

    setDisplayedAvailabilities(availabilitiesToDisplay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilities]);

  return (
    <Card>
      <S.CardHeader data-testid="cardHeader">
        <S.CardTitle>
          <FormattedMessage id="RECURRENT_AVAILABILITY.CARD.TITLE" />
        </S.CardTitle>
        <S.CardSubTitle>
          <FormattedMessage id="RECURRENT_AVAILABILITY.CARD.SUB_TITLE" />
        </S.CardSubTitle>
      </S.CardHeader>
      <S.ContentContainer data-testid="cardBody">
        <S.AvailabilitySections>
          <Text.Medium>
            <FormattedMessage id="RECURRENT_AVAILABILITY.SECTION.NEW_AVAILABILITY" />
          </Text.Medium>
          <S.NewAvailabilityInputsContainer>
            <S.StyledDropdown
              label={intl.formatMessage({
                id: 'RECURRENT_AVAILABILITY.NEW_AVAILABILITY.DAY_LABEL',
              })}
              onChange={(e: { value: string }) => setSelectedDay(e.value)}
              options={[
                {
                  label: intl.formatMessage({
                    id: 'RECURRENT_AVAILABILITY.SECTION.NEW_AVAILABILITY.MONDAY',
                  }),
                  value: 'Monday',
                },
                {
                  label: intl.formatMessage({
                    id: 'RECURRENT_AVAILABILITY.SECTION.NEW_AVAILABILITY.TUESDAY',
                  }),
                  value: 'Tuesday',
                },
                {
                  label: intl.formatMessage({
                    id: 'RECURRENT_AVAILABILITY.SECTION.NEW_AVAILABILITY.WEDNESDAY',
                  }),
                  value: 'Wednesday',
                },
                {
                  label: intl.formatMessage({
                    id: 'RECURRENT_AVAILABILITY.SECTION.NEW_AVAILABILITY.THURSDAY',
                  }),
                  value: 'Thursday',
                },
                {
                  label: intl.formatMessage({
                    id: 'RECURRENT_AVAILABILITY.SECTION.NEW_AVAILABILITY.FRIDAY',
                  }),
                  value: 'Friday',
                },
                {
                  label: intl.formatMessage({
                    id: 'RECURRENT_AVAILABILITY.SECTION.NEW_AVAILABILITY.SATURDAY',
                  }),
                  value: 'Saturday',
                },
                {
                  label: intl.formatMessage({
                    id: 'RECURRENT_AVAILABILITY.SECTION.NEW_AVAILABILITY.SUNDAY',
                  }),
                  value: 'Sunday',
                },
              ]}
            />
            <S.HoursWrapper>
              <S.StyledDropdown
                onChange={(e: { value: string }) =>
                  setSelectedStartHour(e.value)
                }
                options={hoursOptions}
              />
              <S.IntervalDivider />
              <S.StyledDropdown
                onChange={(e: { value: string }) => setSelectedEndHour(e.value)}
                options={hoursOptions}
              />
            </S.HoursWrapper>
          </S.NewAvailabilityInputsContainer>
          <S.StyledButton
            data-testid="addButton"
            onClick={handleCreateNewRecurrentAvailability}
          >
            <FormattedMessage id="RECURRENT_AVAILABILITY.SECTION.NEW_AVAILABILITY.ADD_BUTTON" />
          </S.StyledButton>
        </S.AvailabilitySections>

        <S.AvailabilitySections>
          <Text.Medium>
            <FormattedMessage id="RECURRENT_AVAILABILITY.SECTION.AVAILABILITY" />
          </Text.Medium>
          {displayedAvailabilities &&
            displayedAvailabilities.length > 0 &&
            !isLoading() &&
            displayedAvailabilities.map((availability, i) => (
              <S.StyledBox key={i.toString()} data-testid="availability">
                <S.PrimaryText>
                  {`${intl.formatMessage({
                    id: `RECURRENT_AVAILABILITY.SECTION.NEW_AVAILABILITY.${availability.allow_day_and_time.day.toUpperCase()}`,
                  })}, ${availability.allow_day_and_time.start}`}
                </S.PrimaryText>
                <S.PrepositionText> às </S.PrepositionText>
                <S.PrimaryText>
                  {availability.allow_day_and_time.end}
                </S.PrimaryText>
                <S.DeleteAvailabilityButton
                  data-testid="deleteAvailability"
                  onClick={() => deleteAvailability(availability)}
                >
                  <Close />
                </S.DeleteAvailabilityButton>
              </S.StyledBox>
            ))}
          {isLoading() && (
            <LinesLoading
              lines={
                displayedAvailabilities.length > MAX_LIST_LENGTH
                  ? displayedAvailabilities.length
                  : MAX_LIST_LENGTH
              }
            />
          )}

          {!isLoading() && !(displayedAvailabilities.length > 0) && (
            <>
              <S.AvailabilityEmptyState data-testid="emptyState">
                <FormattedMessage id="RECURRENT_AVAILABILITY.SECTION.AVAILABILITY.EMPTY_STATE" />
              </S.AvailabilityEmptyState>
            </>
          )}
          {availabilities.length > MAX_LIST_LENGTH && (
            <>
              <S.StyledDivisor />
              <S.ListAccordionButton
                icon={isListExpanded ? ChevronUp : ChevronDown}
                onClick={handleAvailabilityListVisualisation}
                data-testid="expandButton"
              >
                {isListExpanded ? (
                  <FormattedMessage id="RECURRENT_AVAILABILITY.AVAILABILITY.SEE_LESS" />
                ) : (
                  <FormattedMessage id="RECURRENT_AVAILABILITY.AVAILABILITY.SEE_MORE" />
                )}
              </S.ListAccordionButton>
            </>
          )}
        </S.AvailabilitySections>
      </S.ContentContainer>
    </Card>
  );
};

export default RecurrentAvailabilityCard;
